import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Plus, ChevronDown, Sparkles, Loader2, Stethoscope, X, Lightbulb } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { SmartICD10Search } from './SmartICD10Search';

/**
 * ChiefComplaintSection - Just the Chief Complaint input (renders ABOVE notes)
 */
export const ChiefComplaintSection = ({
  chiefComplaint,
  setChiefComplaint,
  autoFilledFields,
  setAutoFilledFields,
  onGenerateDocs,
  isGenerating,
  hasNotes
}) => {
  return (
    <div className="space-y-1">
      <Label className={`text-[10px] ${autoFilledFields.chiefComplaint ? 'text-emerald-400' : 'text-muted-foreground'}`}>
        Chief Complaint (what actually happened)
      </Label>
      <div className="flex gap-1.5 items-center">
        <Input 
          placeholder="e.g., low back pain, knee pain, headache" 
          value={chiefComplaint} 
          onChange={(e) => { setChiefComplaint(e.target.value); setAutoFilledFields(prev => ({ ...prev, chiefComplaint: false })); }} 
          className={`h-8 text-sm bg-input border-border flex-1 ${autoFilledFields.chiefComplaint ? 'border-emerald-500/50 ring-1 ring-emerald-500/30' : ''}`} 
          data-testid="chief-complaint-input"
          spellCheck={true}
          autoCorrect="on"
          autoCapitalize="sentences"
        />
        {hasNotes && onGenerateDocs && (
          <Button
            onClick={onGenerateDocs}
            disabled={isGenerating || !hasNotes}
            size="sm"
            className="h-8 w-8 p-0 shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground"
            title={isGenerating ? "Generating..." : "Generate"}
            data-testid="create-docs-chief-btn"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <img src="/emblem_icon.png" alt="" className="w-5 h-5 object-contain" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * COMMON_ICD10_BY_TOPIC — quick-tap chips for the most common ICD-10s
 * grouped by clinical topic. User feedback Feb 15, 2026: "like the PE
 * findings chips, I want a running box of top common ICD-10s I can tap."
 *
 * Strategy: parse chiefComplaint / reasonForVisit + first 200 chars of notes
 * to detect the active topic, then render the matching chip set. Tapping
 * a chip appends to manualIcd10 (deduped).
 */
const COMMON_ICD10_BY_TOPIC = {
  msk: [
    { code: 'M54.50', label: 'Low back pain' },
    { code: 'M54.5', label: 'LBP (legacy)' },
    { code: 'M25.561', label: 'Knee pain R' },
    { code: 'M25.562', label: 'Knee pain L' },
    { code: 'M25.511', label: 'Shoulder pain R' },
    { code: 'M25.512', label: 'Shoulder pain L' },
    { code: 'M79.604', label: 'Limb pain' },
    { code: 'M62.838', label: 'Muscle spasm' },
  ],
  msk_neck: [
    { code: 'M54.2', label: 'Cervicalgia' },
    { code: 'M54.12', label: 'Radiculopathy cervical' },
  ],
  headache: [
    { code: 'G43.909', label: 'Migraine' },
    { code: 'G44.209', label: 'Tension HA' },
    { code: 'R51.9', label: 'HA, unsp' },
  ],
  anxiety_depression: [
    { code: 'F41.1', label: 'GAD' },
    { code: 'F32.9', label: 'MDD unsp' },
    { code: 'F43.10', label: 'PTSD unsp' },
    { code: 'F51.01', label: 'Insomnia' },
    { code: 'F43.23', label: 'Adj d/o w/anx+dep' },
  ],
  uri: [
    { code: 'J06.9', label: 'URI, unsp' },
    { code: 'J00', label: 'Acute nasopharyngitis' },
    { code: 'J02.9', label: 'Acute pharyngitis' },
    { code: 'J20.9', label: 'Acute bronchitis' },
    { code: 'R05.9', label: 'Cough unsp' },
  ],
  htn: [
    { code: 'I10', label: 'Essential HTN' },
    { code: 'R03.0', label: 'Elevated BP reading' },
  ],
  diabetes: [
    { code: 'E11.9', label: 'T2DM w/o comp' },
    { code: 'E11.65', label: 'T2DM w/hyperglyc' },
    { code: 'R73.03', label: 'Prediabetes' },
  ],
  lipids: [
    { code: 'E78.5', label: 'Hyperlipidemia' },
    { code: 'E78.00', label: 'Hypercholesterolemia' },
  ],
  thyroid: [
    { code: 'E03.9', label: 'Hypothyroidism' },
    { code: 'E05.90', label: 'Hyperthyroidism' },
  ],
  anemia: [
    { code: 'D50.9', label: 'Iron def anemia' },
    { code: 'D64.9', label: 'Anemia unsp' },
    { code: 'D51.0', label: 'B12 def anemia' },
  ],
  annual_wellness: [
    { code: 'Z00.00', label: 'Adult exam, no abn' },
    { code: 'Z00.01', label: 'Adult exam w/abn' },
    { code: 'Z71.3', label: 'Diet counseling' },
    { code: 'Z71.82', label: 'Exercise counseling' },
    { code: 'Z71.89', label: 'Other counseling' },
  ],
  lab_review: [
    { code: 'Z00.00', label: 'Lab review' },
    { code: 'Z71.89', label: 'Counseling' },
  ],
  refill: [
    { code: 'Z79.899', label: 'Long-term Rx use' },
    { code: 'Z76.0', label: 'Rx refill encounter' },
  ],
  acne_derm: [
    { code: 'L70.0', label: 'Acne vulgaris' },
    { code: 'L20.9', label: 'Atopic dermatitis' },
    { code: 'L30.9', label: 'Dermatitis unsp' },
  ],
  gerd: [
    { code: 'K21.9', label: 'GERD' },
    { code: 'K59.00', label: 'Constipation' },
    { code: 'R10.9', label: 'Abd pain unsp' },
  ],
  allergies: [
    { code: 'J30.9', label: 'Allergic rhinitis' },
    { code: 'L29.9', label: 'Pruritus' },
  ],
  female: [
    { code: 'N91.2', label: 'Amenorrhea' },
    { code: 'N94.6', label: 'Dysmenorrhea' },
    { code: 'N76.0', label: 'Acute vaginitis' },
  ],
};

/**
 * Detect the active topic from chiefComplaint + reasonForVisit + notes preview.
 * Returns the COMMON_ICD10_BY_TOPIC key or null.
 */
const _detectTopic = (chiefComplaint, reasonForVisit, notes) => {
  const blob = `${chiefComplaint || ''} ${reasonForVisit || ''} ${(notes || '').slice(0, 200)}`.toLowerCase();
  if (/back pain|lbp|knee pain|shoulder pain|joint pain|sprain|strain|spasm|musculoskeletal|\bmsk\b/.test(blob)) {
    if (/neck|cervical/.test(blob)) return 'msk_neck';
    return 'msk';
  }
  if (/headache|migraine|tension ha\b|\bha\b/.test(blob)) return 'headache';
  if (/anxiety|depression|ptsd|gad|insomnia|behavioral|mental health|mood|suicidal/.test(blob)) return 'anxiety_depression';
  if (/uri\b|cold|cough|sinus|pharyngitis|bronchitis|sore throat|upper respiratory/.test(blob)) return 'uri';
  if (/htn|hypertension|blood pressure|bp\s/.test(blob)) return 'htn';
  if (/diabetes|dm\b|a1c|hyperglyc|prediabet/.test(blob)) return 'diabetes';
  if (/hyperlipid|cholesterol|lipid panel|ldl|hdl/.test(blob)) return 'lipids';
  if (/thyroid|hypothyroid|tsh/.test(blob)) return 'thyroid';
  if (/anemia|iron def|low iron|b12|ferritin/.test(blob)) return 'anemia';
  if (/annual|wellness|periodic|pha\b|preventive/.test(blob)) return 'annual_wellness';
  if (/lab review|results review|cbc|cmp|bloodwork/.test(blob)) return 'lab_review';
  if (/refill|medication refill|rx renewal|prescription renew/.test(blob)) return 'refill';
  if (/acne|rash|eczema|dermatitis|skin/.test(blob)) return 'acne_derm';
  if (/gerd|reflux|heartburn|abd pain|constipation/.test(blob)) return 'gerd';
  if (/allergy|allergies|rhinitis|hay fever|pruritus|itching/.test(blob)) return 'allergies';
  if (/dysmenorrhea|amenorrhea|menstrual|gyn|pelvic|vaginitis/.test(blob)) return 'female';
  return null;
};

/**
 * DetectedICD10Inline — Compact strip of detected codes for the input column
 */
export const DetectedICD10Inline = ({ autoDetectedCodes, manualIcd10, setManualIcd10, chiefComplaint, reasonForVisit, notes }) => {
  const addedCodes = useMemo(() => {
    const codes = (manualIcd10 || '').split(',').map(c => c.trim().toUpperCase()).filter(Boolean);
    return new Set(codes);
  }, [manualIcd10]);

  const handleAdd = useCallback((code) => {
    const current = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
    if (!current.includes(code)) {
      setManualIcd10([...current, code].join(', '));
    }
    // Track personal usage so chips re-rank by frequency (Feb 15, 2026 user
    // request: ortho-heavy PCMs should see M54.50 / M25.561 first; BH-heavy
    // PCMs should see F41.1 / F32.9 first). Stored as a plain {code: count}
    // map in localStorage so it survives sessions.
    // Feb 28, 2026 — also mirrored to the user document via POST
    // /api/me/icd10-usage (debounced batch flush) so the ranking now
    // follows the user across devices.
    try {
      const key = 'writeright_icd10_usage_v1';
      const usage = JSON.parse(localStorage.getItem(key) || '{}');
      usage[code] = (usage[code] || 0) + 1;
      localStorage.setItem(key, JSON.stringify(usage));
      // Schedule a debounced server flush of the FULL local map. Merge
      // semantics on the server take max(server, payload) per code so
      // concurrent device taps never lose counts. Flush is debounced
      // 1.5s after the last tap to coalesce bursts.
      window.dispatchEvent(new CustomEvent('writeright:icd10-usage-flush'));
    } catch { /* ignore quota errors */ }
  }, [manualIcd10, setManualIcd10]);

  // Read personal usage map once per render — used to re-rank common chips
  const [usageBump, setUsageBump] = useState(0);
  useEffect(() => {
    const onReset = () => setUsageBump(b => b + 1);
    window.addEventListener('writeright:icd10-usage-reset', onReset);
    return () => window.removeEventListener('writeright:icd10-usage-reset', onReset);
  }, []);
  const personalUsage = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('writeright_icd10_usage_v1') || '{}'); }
    catch { return {}; }
  }, [manualIcd10, usageBump]); // refresh when manualIcd10 changes (after a tap) or reset event fires

  return (
    <div className="space-y-2" data-testid="detected-icd10-inline">
      {/* Detected codes strip */}
      {autoDetectedCodes && autoDetectedCodes.length > 0 && (
        <div className="px-3 py-2 border border-amber-500/20 bg-amber-500/5 rounded-lg">
          <div className="flex items-center gap-2 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-semibold text-muted-foreground">Detected from Notes</span>
            <Badge variant="outline" className="text-[10px] h-4 px-1 bg-secondary border-border text-muted-foreground">{autoDetectedCodes.length}</Badge>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="detected-icd10-inline-list">
            {autoDetectedCodes.map((item) => {
              const added = addedCodes.has(item.code);
              return (
                <div key={item.code}
                  title={`${item.code}: ${item.description || item.desc || 'Unknown'}`}
                  className={`px-2 py-0.5 rounded text-xs border flex items-center gap-1 transition-all cursor-help ${
                    added ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-secondary/50 border-border hover:border-amber-500/50'
                  }`}
                >
                  <code className="font-medium text-foreground text-[11px]">{item.code}</code>
                  <span className="text-muted-foreground truncate max-w-[100px] text-[10px]">{item.description || item.desc || ''}</span>
                  {added ? (
                    <span className="text-[9px] font-bold text-emerald-400">+</span>
                  ) : (
                    <button onClick={() => handleAdd(item.code)}
                      className="shrink-0 cursor-pointer hover:text-emerald-400 text-muted-foreground" title={`Add ${item.code}`}
                      data-testid={`icd10-inline-add-${item.code}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Common ICD-10 quick-add chips — topic-aware + personal-usage re-ranked */}
      {(() => {
        const topic = _detectTopic(chiefComplaint, reasonForVisit, notes);
        if (!topic) return null;
        const chips = COMMON_ICD10_BY_TOPIC[topic] || [];
        if (chips.length === 0) return null;
        const topicLabel = topic.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        // Re-rank: chips with higher personalUsage[code] count come first.
        // Ties broken by original array order (curated clinical priority).
        const ranked = chips
          .map((chip, idx) => ({ ...chip, _idx: idx, _count: personalUsage[chip.code] || 0 }))
          .sort((a, b) => (b._count - a._count) || (a._idx - b._idx));
        const maxCount = ranked[0]?._count || 0;
        return (
          <div className="px-3 py-2 border border-cyan-500/20 bg-cyan-500/5 rounded-lg" data-testid="common-icd10-chips">
            <div className="flex items-center gap-2 mb-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-semibold text-muted-foreground">Common ICD-10s</span>
              <Badge variant="outline" className="text-[10px] h-4 px-1 bg-cyan-500/10 border-cyan-500/40 text-cyan-300">{topicLabel}</Badge>
              {maxCount >= 3 && (
                <span className="text-[9px] text-cyan-400/70 italic">re-ranked by your usage</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {ranked.map((chip) => {
                const present = addedCodes.has(chip.code);
                const isTop = chip._count > 0 && chip._count === maxCount && maxCount >= 3;
                return (
                  <button
                    key={chip.code}
                    type="button"
                    onClick={() => handleAdd(chip.code)}
                    disabled={present}
                    className={`relative text-[10px] px-1.5 py-0.5 rounded-md border transition-colors flex items-center gap-1 ${
                      present
                        ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300 cursor-default opacity-60'
                        : isTop
                          ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-500/70 ring-1 ring-cyan-500/20'
                          : 'bg-secondary/60 border-border text-muted-foreground hover:bg-cyan-500/15 hover:border-cyan-500/40 hover:text-cyan-300'
                    }`}
                    title={present
                      ? `${chip.code} already added`
                      : `Add ${chip.code} — ${chip.label}${chip._count > 0 ? ` · used ${chip._count}× by you` : ''}`}
                    data-testid={`icd10-chip-${chip.code}`}
                  >
                    {isTop && !present && <span aria-hidden="true">⭐</span>}
                    <code className="font-mono font-semibold">{chip.code}</code>
                    <span className="text-[9px] opacity-70">{chip.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
      {/* Manual ICD-10 input row */}
      <div className="flex items-center gap-2">
        <Label className="text-foreground font-bold text-xs flex items-center gap-1 shrink-0 whitespace-nowrap">
          <Plus className="w-3 h-3 text-primary" />Add ICD-10
        </Label>
        <Input
          placeholder="e.g., M54.5, G43.909, F32.1"
          value={manualIcd10}
          onChange={(e) => setManualIcd10(e.target.value.toUpperCase())}
          onFocus={(e) => {
            // Mar 3, 2026 — Provider reported that after entering the 1st
            // ICD-10 code, subsequent characters were being inserted at
            // position 0 (in front of the 1st code) rather than appended.
            // Root cause is a browser-quirky focus lifecycle where the
            // cursor lands at index 0 on refocus after a re-render. Force
            // cursor to end whenever the field has content.
            const val = e.target.value || '';
            if (val.length > 0) {
              requestAnimationFrame(() => {
                try { e.target.setSelectionRange(val.length, val.length); } catch (_) { /* ignore */ }
              });
            }
          }}
          className="h-8 text-xs bg-input border-border font-mono flex-1"
          data-testid="icd10-input-inline"
        />
      </div>
    </div>
  );
};

/**
 * ICD10Section - ICD-10 code input, smart search, and suggestions (renders BELOW notes)
 */
export const ICD10Section = ({
  manualIcd10,
  setManualIcd10,
  autoDetectedCodes,
  liveIcd10Suggestions,
  primaryIcd10Codes,
  setPrimaryIcd10Codes,
  onReorderSuggestions,
  onDeleteSuggestion
}) => {
  const [smartSearchOpen, setSmartSearchOpen] = useState(true);

  // Which codes are already added to the manual input
  const addedCodes = useMemo(() => {
    return new Set((manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean));
  }, [manualIcd10]);

  const handleSmartSearchSelect = useCallback((result, isAdding) => {
    if (isAdding) {
      const currentCodes = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
      if (!currentCodes.includes(result.code)) {
        const newCodes = [...currentCodes, result.code].join(', ');
        setManualIcd10(newCodes);
      }
    } else {
      const currentCodes = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
      const newCodes = currentCodes.filter(c => c !== result.code).join(', ');
      setManualIcd10(newCodes);
    }
  }, [manualIcd10, setManualIcd10]);

  const handleAddDetectedCode = useCallback((code) => {
    const currentCodes = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
    if (!currentCodes.includes(code)) {
      setManualIcd10([...currentCodes, code].join(', '));
    }
  }, [manualIcd10, setManualIcd10]);

  // Filter out detected codes that are already added
  const unadded = useMemo(() => {
    return (autoDetectedCodes || []).filter(s => !addedCodes.has(s.code));
  }, [autoDetectedCodes, addedCodes]);

  return (
    <>
      {/* 1. Auto-Detected Suggestions — display only, + to add */}
      {autoDetectedCodes && autoDetectedCodes.length > 0 && (
        <Collapsible defaultOpen={true}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-7 px-2" data-testid="detected-icd10-toggle">
              <span className="flex items-center gap-2 text-xs">
                <Lightbulb className="w-3 h-3 text-amber-400" />
                <span>Detected from Notes</span>
                <Badge variant="outline" className="text-[10px] h-4 px-1 bg-secondary border-border text-muted-foreground">{autoDetectedCodes.length}</Badge>
              </span>
              <ChevronDown className="w-3 h-3" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="detected-icd10-list">
              {autoDetectedCodes.map((item) => {
                const alreadyAdded = addedCodes.has(item.code);
                return (
                  <div key={item.code}
                    title={`${item.code}: ${item.description || item.desc || 'Unknown'}`}
                    className={`px-2 py-1 rounded text-xs border flex items-center gap-1.5 transition-all cursor-help ${
                      alreadyAdded 
                        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' 
                        : 'bg-secondary/50 border-border hover:border-amber-500/50'
                    }`}
                  >
                    <code className="font-medium text-foreground">{item.code}</code>
                    <span className="text-muted-foreground truncate max-w-[120px]">{item.description || item.desc || ''}</span>
                    {alreadyAdded ? (
                      <span className="text-[9px] font-bold text-emerald-400" data-testid={`icd10-added-${item.code}`}>Added</span>
                    ) : (
                      <button
                        onClick={() => handleAddDetectedCode(item.code)}
                        className="shrink-0 cursor-pointer hover:text-emerald-400 text-muted-foreground transition-colors"
                        title={`Add ${item.code} to note`}
                        data-testid={`icd10-add-${item.code}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* 2. Smart ICD-10 Search — Open by default */}
      <Collapsible open={smartSearchOpen} onOpenChange={setSmartSearchOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-between h-8 px-3 border-dashed border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/5"
            data-testid="smart-icd10-toggle"
          >
            <span className="flex items-center gap-2 text-xs">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span className="elegant-label text-xs">Smart ICD-10 Search</span>
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-amber-500/10 border-amber-500/30 text-amber-400">
                AI
              </Badge>
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${smartSearchOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-3 bg-secondary/30 border border-amber-500/20 rounded-lg">
            <SmartICD10Search 
              onSelectCode={handleSmartSearchSelect}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* 3. Manual ICD-10 Entry */}
      <div className="space-y-1.5">
        <Label className="text-foreground font-bold text-xs flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5 text-primary" />Add ICD-10 Codes<span className="text-muted-foreground font-normal">(comma separated)</span>
        </Label>
        <Input 
          placeholder="e.g., M54.5, G43.909, F32.1" 
          value={manualIcd10} 
          onChange={(e) => setManualIcd10(e.target.value.toUpperCase())} 
          onFocus={(e) => {
            // Mar 3, 2026 — Same defensive fix as icd10-input-inline
            // (force cursor to end on focus so subsequent characters
            // always append rather than insert at position 0).
            const val = e.target.value || '';
            if (val.length > 0) {
              requestAnimationFrame(() => {
                try { e.target.setSelectionRange(val.length, val.length); } catch (_) { /* ignore */ }
              });
            }
          }}
          className="h-9 text-sm bg-input border-border font-mono" 
          data-testid="icd10-input" 
        />
      </div>
    </>
  );
};

// Keep backward compatibility - the combined component
export const ProviderICD10Section = (props) => (
  <>
    <ChiefComplaintSection
      chiefComplaint={props.chiefComplaint}
      setChiefComplaint={props.setChiefComplaint}
      autoFilledFields={props.autoFilledFields}
      setAutoFilledFields={props.setAutoFilledFields}
    />
    <ICD10Section
      manualIcd10={props.manualIcd10}
      setManualIcd10={props.setManualIcd10}
      liveIcd10Suggestions={props.liveIcd10Suggestions}
      primaryIcd10Codes={props.primaryIcd10Codes}
      setPrimaryIcd10Codes={props.setPrimaryIcd10Codes}
      onReorderSuggestions={props.onReorderSuggestions}
      onDeleteSuggestion={props.onDeleteSuggestion}
    />
  </>
);

export default ProviderICD10Section;
